import * as React from 'react';
import clsx from 'clsx';
import { ShareHeader } from './ShareHeader';
import { Button } from '@/components/ui/Button';
import { ResponsiveShareModal } from './ResponsiveShareModal';
import { SHARE_SOCIALS, isMobileDevice } from '@/utils/share';

interface SocialShareProps {
  onShare: (platform: string) => void;
  onNativeShare: () => void;
  link: string;
  onCopy: () => void;
}

/** Social media share icons (desktop) or native share button (mobile/tablet). */
export const SocialShare: React.FC<SocialShareProps> = ({
  onShare,
  onNativeShare,
  link,
  onCopy,
}) => {
  const [isModalOpen, setIsModalOpen] = React.useState(false);

  const handleMobileShare = () => {
    if (isMobileDevice() && 'share' in navigator) {
      onNativeShare();
    } else {
      setIsModalOpen(true);
    }
  };

  return (
    <div className='flex w-full flex-col gap-6 md:gap-10'>
      <ShareHeader title='Share on Social Media' />

      {/* Desktop View: Icons Row */}
      <div className='hidden items-start gap-8 md:flex'>
        {SHARE_SOCIALS.map((social) => (
          <button
            key={social.id}
            onClick={() => onShare(social.id)}
            className='flex flex-col items-center gap-1 transition-transform hover:scale-105'
            aria-label={`Share on ${social.name}`}
          >
            <div className='size-[40px]'>
              <img src={social.icon} alt={social.name} className='size-full object-contain' />
            </div>
            <span className='font-sans text-11 font-medium leading-21 tracking-normal text-gray-670'>
              {social.name}
            </span>
          </button>
        ))}
      </div>

      {/* Mobile View: Full Width Button */}
      <div className='block md:hidden'>
        <Button
          onClick={handleMobileShare}
          variant='purple-dark'
          className={clsx(
            'h-[50px] w-full rounded-20',
            'text-brand-800',
            'font-sans text-20 font-bold uppercase leading-32 tracking-normal',
            'hover:brightness-95'
          )}
        >
          SHARE ON SOCIAL MEDIA
        </Button>
      </div>

      {/* Responsive Modal (fallback when navigator.share unavailable) */}
      <ResponsiveShareModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        socials={SHARE_SOCIALS}
        onShare={(platform) => {
          onShare(platform);
          setIsModalOpen(false);
        }}
        link={link}
        onCopy={onCopy}
      />
    </div>
  );
};
